import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Mail, MailX, RefreshCw } from "lucide-react";
import { Seo } from "../components/Seo";
import { Logo } from "../components/Logo";
import { STATES } from "../data/states";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "abslpay_admin_token";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

const statusStyles = {
  new: "bg-emerald-50 text-emerald-800 border-emerald-200",
  contacted: "bg-amber-50 text-amber-800 border-amber-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto px-4 py-24">
      <div className="flex justify-center"><Logo /></div>
      <h1 className="mt-8 font-display text-2xl font-extrabold text-center text-slate-900">Admin sign in</h1>
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
        <input
          data-testid="admin-email-input"
          type="email" required placeholder="Email" autoComplete="email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        <input
          data-testid="admin-password-input"
          type="password" required placeholder="Password" autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
        <button
          type="submit" disabled={busy} data-testid="admin-login-submit"
          className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-slate-900 text-white font-bold px-6 py-3 hover:bg-emerald-700 transition-colors disabled:opacity-60"
        >
          {busy && <Loader2 size={15} className="animate-spin" />} Sign in
        </button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: authHeaders() });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      if (!localStorage.getItem(TOKEN_KEY)) { setUser(false); setChecking(false); return; }
      try {
        const { data } = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
        setUser(data);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      } finally {
        setChecking(false);
      }
    };
    init();
  }, []);

  useEffect(() => { if (user) loadLeads(); }, [user, loadLeads]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status }, { headers: authHeaders() });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      toast.error("Failed to update status");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  if (checking) {
    return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-emerald-600" size={28} /></div>;
  }

  const visible = filter === "all" ? leads : leads.filter((l) => (l.status || "new") === filter);
  const counts = leads.reduce((acc, l) => { const s = l.status || "new"; acc[s] = (acc[s] || 0) + 1; return acc; }, {});

  return (
    <>
      <Seo title="Leads Dashboard | ABSLpay Admin" description="Private admin dashboard." path="/admin" noindex />
      {!user ? (
        <LoginForm onLogin={setUser} />
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl font-extrabold text-slate-900">Quote requests</h1>
              <p className="mt-1 text-sm text-slate-500">{leads.length} total &middot; signed in as {user.email}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={loadLeads} data-testid="admin-refresh-btn"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:border-slate-900 transition-colors">
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
              <button onClick={logout} data-testid="admin-logout-btn"
                className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-bold hover:bg-emerald-700 transition-colors">
                <LogOut size={14} /> Sign out
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {["all", "new", "contacted", "closed"].map((s) => (
              <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`}
                className={`rounded-full px-4 py-2 text-sm font-bold capitalize transition-colors ${
                  filter === s ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-slate-900"
                }`}>
                {s}{s !== "all" && ` (${counts[s] || 0})`}
              </button>
            ))}
          </div>

          <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[980px] text-sm" data-testid="admin-leads-table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                  <th className="p-4">Lead</th>
                  <th className="p-4">Contact</th>
                  <th className="p-4">State</th>
                  <th className="p-4">People</th>
                  <th className="p-4">Provider</th>
                  <th className="p-4">Plan</th>
                  <th className="p-4">Received</th>
                  <th className="p-4">Emailed</th>
                  <th className="p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 last:border-0 align-top" data-testid={`admin-lead-row-${l.id}`}>
                    <td className="p-4">
                      <div className="font-bold text-slate-900">{l.business}</div>
                      <div className="text-slate-500">{l.name}</div>
                      {l.message && <div className="mt-1 max-w-[220px] text-xs text-slate-400">{l.message}</div>}
                    </td>
                    <td className="p-4">
                      <div className="text-slate-700">{l.email}</div>
                      <div className="text-slate-500">{l.phone || "—"}</div>
                    </td>
                    <td className="p-4 font-mono-num font-bold text-slate-700">{STATES[l.state] ? l.state : l.state || "—"}</td>
                    <td className="p-4 font-mono-num text-slate-700">{l.headcount}</td>
                    <td className="p-4 text-slate-600">{l.current_provider || "—"}</td>
                    <td className="p-4 capitalize text-slate-600">{l.plan}</td>
                    <td className="p-4 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                    <td className="p-4">{l.email_sent ? <Mail size={16} className="text-emerald-600" /> : <MailX size={16} className="text-slate-300" />}</td>
                    <td className="p-4">
                      <select
                        value={l.status || "new"}
                        onChange={(e) => setStatus(l.id, e.target.value)}
                        data-testid={`admin-status-select-${l.id}`}
                        className={`rounded-full border px-3 py-1.5 text-xs font-bold capitalize focus:outline-none ${statusStyles[l.status || "new"]}`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={9} className="p-10 text-center text-slate-400">No leads{filter !== "all" ? ` with status "${filter}"` : " yet"}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
